import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { getCheckoutStatus } from "@/lib/api";

const POLL_INTERVAL = 2000;
const MAX_POLLS = 10;

const C = {
  bg: "#F7F7F5",
  bgSoft: "#FFFFFF",
  ink: "#0F0F0F",
  inkSoft: "#4A4A4A",
  inkMuted: "#8A8A8A",
  accent: "#2D5544",
  accentHover: "#3A6B57",
  coral: "#B85042",
  border: "#E5E5E5",
};

const LATO = { fontFamily: "'Lato', sans-serif" };

export default function SuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");

  const poll = useCallback(
    async (attempt = 0) => {
      if (!sessionId) {
        setState("error");
        return;
      }
      if (attempt >= MAX_POLLS) {
        setState("timeout");
        return;
      }
      try {
        const data = await getCheckoutStatus(sessionId);
        if (data.payment_status === "paid") {
          try {
            if (typeof window !== "undefined" && typeof window.gtag === "function" && !window.__dnots_purchase_tracked) {
              window.__dnots_purchase_tracked = true;
              window.gtag("event", "purchase", {
                transaction_id: sessionId,
                currency: "EUR",
                value: 47,
                items: [{ item_id: "dnots_launch", item_name: "The Dark Night of the Soul Guide", price: 47, quantity: 1 }],
              });
            }
          } catch (e) {
            // GA not loaded — silent fail
          }
          setState("paid");
          return;
        }
        if (data.status === "expired") {
          setState("expired");
          return;
        }
        setTimeout(() => poll(attempt + 1), POLL_INTERVAL);
      } catch (e) {
        console.error(e);
        setState("error");
      }
    },
    [sessionId]
  );

  useEffect(() => {
    poll(0);
  }, [poll]);

  return (
    <main
      className="min-h-screen flex items-center justify-center px-6"
      data-testid="success-page"
      style={{ background: C.bg, color: C.ink, ...LATO }}
    >
      <div className="max-w-2xl w-full text-center py-24">
        {state === "checking" && (
          <div data-testid="status-checking">
            <Loader2
              className="mx-auto animate-spin"
              size={42}
              style={{ color: C.accent }}
            />
            <h1
              style={{
                ...LATO,
                fontWeight: 700,
                fontSize: "clamp(36px, 5vw, 64px)",
                lineHeight: 1,
                letterSpacing: "-0.02em",
                marginTop: 40,
                color: C.ink,
              }}
            >
              Verifying your passage…
            </h1>
            <p
              style={{
                ...LATO,
                fontSize: 17,
                lineHeight: 1.6,
                color: C.inkSoft,
                marginTop: 24,
              }}
            >
              Hold for a moment. We're confirming your payment with Stripe.
            </p>
          </div>
        )}

        {state === "paid" && (
          <div data-testid="status-paid">
            <CheckCircle2 className="mx-auto" size={48} style={{ color: C.accent }} />
            <div
              style={{
                ...LATO,
                fontSize: 11,
                fontWeight: 500,
                letterSpacing: "0.3em",
                textTransform: "uppercase",
                color: C.coral,
                marginTop: 32,
              }}
            >
              The Door Opens
            </div>
            <h1
              style={{
                ...LATO,
                fontWeight: 700,
                fontSize: "clamp(40px, 6vw, 80px)",
                lineHeight: 1,
                letterSpacing: "-0.02em",
                marginTop: 24,
                color: C.ink,
              }}
            >
              Welcome to the{" "}
              <span style={{ fontStyle: "italic", color: C.accent }}>
                descent.
              </span>
            </h1>
            <p
              className="max-w-xl mx-auto"
              style={{
                ...LATO,
                fontSize: 18,
                lineHeight: 1.6,
                color: C.ink,
                marginTop: 32,
              }}
            >
              Your access to{" "}
              <em style={{ fontStyle: "normal", fontWeight: 700 }}>
                The Dark Night of the Soul
              </em>{" "}
              has been confirmed. A welcome email with the guide link is on
              its way. Open it when you are ready — and only then.
            </p>
            <Link
              to="/"
              className="mt-12 inline-flex items-center gap-4 transition-colors duration-300"
              style={{
                background: C.accent,
                color: C.bg,
                padding: "16px 32px",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = C.accentHover)
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = C.accent)
              }
              data-testid="back-home-button"
            >
              <span
                style={{
                  ...LATO,
                  fontSize: 12,
                  fontWeight: 600,
                  letterSpacing: "0.3em",
                  textTransform: "uppercase",
                }}
              >
                Return Home
              </span>
            </Link>
          </div>
        )}

        {(state === "expired" || state === "error" || state === "timeout") && (
          <div data-testid={`status-${state}`}>
            <XCircle className="mx-auto" size={42} style={{ color: C.coral }} />
            <h1
              style={{
                ...LATO,
                fontWeight: 700,
                fontSize: "clamp(32px, 4.5vw, 56px)",
                lineHeight: 1.1,
                letterSpacing: "-0.02em",
                marginTop: 32,
                color: C.ink,
              }}
            >
              Something didn't go through.
            </h1>
            <p
              style={{
                ...LATO,
                fontSize: 17,
                lineHeight: 1.6,
                color: C.inkSoft,
                marginTop: 24,
              }}
            >
              {state === "expired"
                ? "Your checkout session expired."
                : state === "timeout"
                ? "We couldn't confirm the payment in time. If you were charged, please check your email — otherwise try again."
                : "We hit an issue confirming your payment."}
            </p>
            <Link
              to="/"
              className="mt-10 inline-flex items-center gap-4 transition-colors"
              style={{
                border: `1px solid ${C.border}`,
                color: C.ink,
                padding: "16px 32px",
                background: "transparent",
              }}
            >
              <span
                style={{
                  ...LATO,
                  fontSize: 12,
                  fontWeight: 600,
                  letterSpacing: "0.3em",
                  textTransform: "uppercase",
                }}
              >
                Try Again
              </span>
            </Link>
          </div>
        )}
      </div>
    </main>
  );
}
